import React, { useState, useEffect, useRef } from 'react';
import { Input, InputContainer } from '../TextInput/TextInput.styled';
import PlacesAutocomplete, { geocodeByAddress, geocodeByPlaceId, getLatLng } from 'react-places-autocomplete';
import { DropDownListContainer, DropDownList, ListItem } from '../DropDownInput/DrowDownInput.styles';
import { Container, ErrorMessage, IconContainer, AddressIcon, ErrorIcon } from './AddressAutoComplete.styled';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import _ from 'lodash';
import { formatOccupancyDate } from '@/utils/handleDate';

declare global {
  interface Window {
    google: {
      maps: {
        places: any;
      };
    };
  }
}

export interface AddressAutoCompleteProps {
  error?: string;
  size?: string;
  isDirection?: boolean;
  value?: any;
  typeOfIcon?: string;
  isAddressIcon?: boolean;
  iconColor?: string;
  propertyType?: string;
  isMailingAddress?: boolean;
  onChange?: (value: string, propertyType?: any, index?: number) => void;
  isDisabled?: boolean;
}

interface AddressComponent {
  types: string[];
  short_name?: string;
}

interface FormattedAddress {
  streetAddress: string;
  city: string;
  state: string;
  county?: string;
  zip: string;
  latitude: number;
  longitude: number;
  streetNumber?: string;
  route?: string;
  locality?: string;
  stateOrProvince?: string;
  country?: string;
  postalCode?: string;
  occupancyDate?: string;
}

export const parseAddress = (addressComponents: AddressComponent[]): Partial<FormattedAddress> => {
  const findComponent = (type: string) => addressComponents?.find((component) => component?.types?.includes(type));

  const streetNumber = findComponent('street_number');
  const route = findComponent('route');
  const locality =
    findComponent('locality') ||
    findComponent('postal_town') ||
    findComponent('administrative_area_level_3') ||
    findComponent('sublocality_level_1') ||
    findComponent('sublocality') ||
    findComponent('neighborhood');
  const stateOrProvince = findComponent('administrative_area_level_1');
  const county = findComponent('administrative_area_level_2');
  const country = findComponent('country');
  const postalCode = findComponent('postal_code');

  return {
    streetNumber: streetNumber?.short_name,
    route: route?.short_name,
    locality: locality?.short_name,
    stateOrProvince: stateOrProvince?.short_name,
    county: county?.short_name,
    country: country?.short_name,
    postalCode: postalCode?.short_name,
  };
};

export const AddressAutoComplete: React.FC<AddressAutoCompleteProps> = ({
  size = '12rem',
  isDirection = false,
  value = '',
  typeOfIcon,
  iconColor,
  isMailingAddress = false,
  propertyType = 'text',
  onChange,
  isDisabled = false,
}) => {
  const [address, setAddress] = useState(value);
  const [focused, setFocused] = useState(false);
  const [inputError, setInputError] = useState(false);
  const [validated, setValidated] = useState(false);
  const [visited, setVisited] = useState(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const isRequired = true;
  const dispatch = useAppDispatch();
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  const hasIcon = typeOfIcon === 'address';
  const currentForm = useAppSelector((state) => state.CurrentForm);
  const isHidden = templateConfig?.pages?.LetsGetStarted?.consumerInformation?.addressSearch?.isHidden;
  const { isResume } = useAppSelector((state) => state.currentPage);
  const { address: addressForm, city, state, zip } = currentForm;
  const isICE = templateConfig.isICE ?? false;
  const hasAutoPrefilledRef = useRef(false);
  const prevSelectionRef = useRef<string>('');

  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleMapsLoaded(true);
      } else {
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  useEffect(() => {
    if (!focused && visited) {
      setInputError(isRequired && visited && !validated);
    }
  }, [focused, validated, visited, isRequired]);

  useEffect(() => {
    if (value) {
      setAddress(value);
    }
  }, [value]);

  const onBlur = () => {
    setFocused(false);
    setValidated(address?.length > 0);
  };
  const handleChange = (address: string) => {
    setAddress(address);
    if (onChange) {
      onChange(address, propertyType);
    }

    if (address.length < 4) {
      hasAutoPrefilledRef.current = false;
    }
  };

  useEffect(() => {
    if (hasAutoPrefilledRef.current) return;
    if (visited) return;
    if (!isMailingAddress && addressForm && !isResume && !isDirection) {
      const lower = addressForm.toLowerCase();
      const hasCity = city ? lower.includes(String(city).toLowerCase()) : false;
      const hasState = state ? lower.includes(String(state).toLowerCase()) : false;
      const hasZip = zip ? lower.includes(String(zip).toLowerCase()) : false;
      const parts = [addressForm];
      if (!hasCity && city) parts.push(city);
      if (!hasState && state) parts.push(state);
      if (!hasZip && zip) parts.push(zip);
      const selectionNormalized = parts.join(', ');
      const hasPostal = /\b\d{5}(?:-\d{4})?\b/.test(selectionNormalized);
      const hasStateAbbr = /,\s*[A-Z]{2}\b/.test(selectionNormalized);
      const hasStreetNumber = /\d/.test(selectionNormalized.split(',')[0] || '');
      if (hasStreetNumber && (hasPostal || hasStateAbbr)) {
        hasAutoPrefilledRef.current = true;
        handleAddressSelect(selectionNormalized, '', true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressForm, city, state, zip, isDirection, isMailingAddress, isResume, visited]);

  const handleFocus = () => {
    setFocused(true);
    setVisited(true);
  };

  const addressIsComplete = (addressObject: Partial<FormattedAddress>) => Object.values(addressObject).every((value) => value);

  const handleAddressSelect = async (selection: string, placeId: string, isAutoPrefill = false) => {
    if (!isResume) {
      try {
        if (!placeId && prevSelectionRef.current && prevSelectionRef.current === selection) {
          return;
        }
        if (!placeId) {
          const hasPostal = /\b\d{5}(?:-\d{4})?\b/.test(selection);
          const hasStateAbbr = /,\s*[A-Z]{2}\b/.test(selection);
          const hasStreetNumber = /\d/.test(selection.split(',')[0] || '');
          if (!hasStreetNumber || (!hasPostal && !hasStateAbbr)) {
            return;
          }
        }
        const results = await geocodeByAddress(selection);
        prevSelectionRef.current = selection;
        let coordinates = await getLatLng(results[0]);
        let addressObject = parseAddress(results[0].address_components);

        try {
          const components: Array<{ types?: string[] }> = (results[0]?.address_components as Array<{ types?: string[] }>) || [];
          components.length; // noop to satisfy try block without logs
        } catch (_) {}
        if (!addressIsComplete(addressObject)) {
          const resultsByAddress = await geocodeByPlaceId(placeId);
          const alternativeAddressObject = parseAddress(resultsByAddress[0].address_components);

          if (addressIsComplete(alternativeAddressObject)) {
            coordinates = await getLatLng(resultsByAddress[0]);
            addressObject = alternativeAddressObject;
          }
        }

        // Build street address without duplicating city/state/zip from selection, prefer components when available
        const streetAddress = addressObject.streetNumber && addressObject.route ? `${addressObject.streetNumber} ${addressObject.route}` : selection?.split(', ')[0] || '';

        // Fall back city from sublocality/neighborhood/postal_town if locality missing, or keep previously entered city
        const parts: Array<{ types?: string[]; short_name?: string }> = (results[0]?.address_components as Array<{ types?: string[]; short_name?: string }>) || [];
        const fallbackCity =
          parts.find((c) => (c?.types || []).includes('sublocality') || (c?.types || []).includes('sublocality_level_1'))?.short_name ||
          parts.find((c) => (c?.types || []).includes('postal_town'))?.short_name ||
          parts.find((c) => (c?.types || []).includes('neighborhood'))?.short_name ||
          '';
        const resolvedCity = addressObject.locality || fallbackCity || currentForm?.city || currentForm?.propertyCity || '';
        const formattedAddress: FormattedAddress = {
          streetAddress,
          city: resolvedCity,
          state: addressObject.stateOrProvince || '',
          county: isICE ? addressObject?.county?.replace(/county/i, '').trim() : addressObject.county || '',
          zip: addressObject.postalCode || '',
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          occupancyDate: addressObject.occupancyDate || currentForm.dataOccupancyDate,
        };

        if (!formattedAddress.city) {
          try {
            const parts: Array<{ types?: string[]; short_name?: string }> = (results[0]?.address_components as Array<{ types?: string[]; short_name?: string }>) || [];
            parts.length;
          } catch (_) {}
        }

        const occupancyDateFormat = formattedAddress?.occupancyDate ? formatOccupancyDate(formattedAddress?.occupancyDate) : '';
        let auxLoanApplication = _.cloneDeep(loanApplication);

        if (isMailingAddress) {
          auxLoanApplication.mailingAddress = {
            mailingStreetAddress: formattedAddress?.streetAddress || '',
            mailingStreetAddress2: '',
            mailingCity: formattedAddress?.city || '',
            mailingState: formattedAddress?.state || '',
            mailingCountry: 'United States',
            mailingCounty: formattedAddress?.county || '',
            mailingZip: formattedAddress?.zip || '',
            mailingLatitude: formattedAddress?.latitude || 0,
            mailingLongitude: formattedAddress?.longitude || 0,
          };
          auxLoanApplication.propertyIsMailingAddress = false;
        } else {
          const existingPropertyStreetAddress2 = auxLoanApplication?.borrowerProperty?.propertyStreetAddress2;

          auxLoanApplication.borrowerProperty = {
            ...auxLoanApplication.borrowerProperty,
            propertyStreetAddress: formattedAddress?.streetAddress || '',
            propertyStreetAddress2: existingPropertyStreetAddress2 || '',
            propertyCity: formattedAddress?.city || '',
            propertyState: formattedAddress?.state || '',
            propertyCountry: 'United States',
            propertyCounty: formattedAddress?.county || '',
            propertyZip: formattedAddress?.zip || '',
            propertyLatitude: formattedAddress?.latitude || 0,
            propertyLongitude: formattedAddress?.longitude || 0,
            propertyOccupancyDate: occupancyDateFormat,
            city: formattedAddress?.city || '',
            state: formattedAddress?.state || '',
            zip: formattedAddress?.zip || '',
          };

          if (auxLoanApplication.propertyIsMailingAddress) {
            auxLoanApplication.mailingAddress = {
              mailingStreetAddress: formattedAddress?.streetAddress || '',
              mailingStreetAddress2: '',
              mailingCity: formattedAddress?.city || '',
              mailingState: formattedAddress?.state || '',
              mailingCountry: 'United States',
              mailingCounty: formattedAddress?.county || '',
              mailingZip: formattedAddress?.zip || '',
              mailingLatitude: formattedAddress?.latitude || 0,
              mailingLongitude: formattedAddress?.longitude || 0,
            };
          }
        }

        const payload = isMailingAddress ? auxLoanApplication.mailingAddress : auxLoanApplication.borrowerProperty;
        dispatch({ type: 'CurrentForm/addField', payload: payload });

        // Also update the county field that GetStarted component expects
        if (!isMailingAddress && formattedAddress?.county) {
          const shouldUpdateCounty = !isAutoPrefill || !currentForm.county;
          if (shouldUpdateCounty) {
            dispatch({
              type: 'CurrentForm/addField',
              payload: { county: formattedAddress.county },
            });
          }
        }

        dispatch({
          type: 'LoanApplication/updateValue',
          payload: { ...auxLoanApplication },
        });

        placeId?.length && setAddress(streetAddress);
      } catch (error) {
        console.error(JSON.stringify({ tag: 'AddressAutoComplete', event: 'handleAddressSelect.error', message: String(error) }));
      }
    }
  };
  useEffect(() => {
    setAddress(value || '');
  }, [isResume, value]);

  if (!isGoogleMapsLoaded) {
    return (
      <Container $isDirection={isDirection} size={size} $isHidden={isHidden}>
        <InputContainer $hasError={inputError}>
          {typeOfIcon && <IconContainer>{<AddressIcon $iconColor={iconColor} />}</IconContainer>}
          <Input
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.addressSearch?.text || 'Loading address search...'}
            size={size}
            $hasError={inputError}
            $focused={focused}
            data-testid="address-input"
            value={address}
            $isAddressIcon={hasIcon}
            $placeholderColor="#aaafb7"
            disabled={true}
          />
        </InputContainer>
      </Container>
    );
  }

  return (
    <PlacesAutocomplete value={address} onChange={handleChange} onSelect={handleAddressSelect}>
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <Container $isDirection={isDirection} size={size} $isHidden={isHidden}>
          <InputContainer $hasError={inputError}>
            {typeOfIcon && <IconContainer>{<AddressIcon $iconColor={iconColor} />}</IconContainer>}
            <Input
              {...getInputProps({
                className: 'location-search-input',
                onFocus: handleFocus,
                onBlur: onBlur,
                disabled: isDisabled,
              })}
              placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.addressSearch?.text || 'Search for property address'}
              size={size}
              $hasError={inputError}
              $focused={focused}
              data-testid="address-input"
              value={address}
              $isAddressIcon={hasIcon}
              $placeholderColor="#aaafb7"
            />
          </InputContainer>
          {inputError && (
            <ErrorMessage>
              <ErrorIcon /> You must provide an address
            </ErrorMessage>
          )}

          {suggestions && suggestions.length > 0 && (
            <>
              <DropDownListContainer>
                <DropDownList size={size} $hasError={false} data-testid="address-drop-down">
                  {loading && <div>Loading...</div>}
                  {suggestions?.map((suggestion) => {
                    const className = suggestion.active ? 'suggestion-item--active' : 'suggestion-item';
                    const style = suggestion.active ? { backgroundColor: '#fafafa', cursor: 'pointer' } : { backgroundColor: '#ffffff', cursor: 'pointer' };
                    return (
                      <ListItem data-testid="address-list" {...getSuggestionItemProps(suggestion, { className, style })} key={suggestion.placeId}>
                        {suggestion.description}
                      </ListItem>
                    );
                  })}
                </DropDownList>
              </DropDownListContainer>
              ,
            </>
          )}
        </Container>
      )}
    </PlacesAutocomplete>
  );
};
